import { Component, computed, inject, Injector, OnInit, signal, viewChild, ChangeDetectionStrategy } from '@angular/core';
import { BaseComponent } from 'app/layout/common/base/base.component';
import { locale as portuguese } from 'app/modules/production/order/detail/clinic-message/i18n/pt';
import { locale as english } from 'app/modules/production/order/detail/clinic-message/i18n/en';
import { locale as spanish } from 'app/modules/production/order/detail/clinic-message/i18n/es';
import { effectQueryParams } from 'app/core/helpers/router.helper';
import { OrderDetailViewService } from 'app/modules/production/order/detail/order-detail-view.service';
import { IEmitJoinRoomClinicMessageSocket } from 'app/core/models/message/message';
import { ListClinicMessageComponent } from 'app/modules/production/order/detail/clinic-message/list-clinic-message/list-clinic-message.component';
import { KInputMessageComponent } from 'app/modules/production/order/detail/clinic-message/k-input-message/k-input-message.component';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { AvatarComponent } from 'ngx-avatars';
import { first, Observable, of, startWith } from 'rxjs';
import { ConvertHelper } from 'app/core/helpers/convert.helper';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs/operators';
import { AuthenticationService } from 'app/core/services/authentication/authentication.service';
import { TranslatePipe } from '@ngx-translate/core';
import { MatIcon } from '@angular/material/icon';
import { MatMenu, MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { EmptyComponent, IModalConfig, ModalService, ToastrService } from '@kiwid-app/kiwid-ui-lib-angular';
import { DragDropDirective, FileHandle } from 'app/core/directive';
import { HashHelper } from 'app/core/helpers/hash.helper';
import { FileHelper } from 'app/core/helpers/file.helper';
import { DomSanitizer } from '@angular/platform-browser';
import { ComponentPortal } from '@angular/cdk/portal';
import { IMediaPreview, ModalPreviewComponent } from 'app/modules/production/order/detail/chat/modal-preview/modal-preview.component';
import { MAX_FILE_SIZE } from 'app/core/const/file.const';
import { EMediaProvider } from 'app/core/enums/media-type.enum';
import { KDateAdd, KDateIs } from '@kiwid-app/kiwid-date-typescript';
import { parseDateWithTime } from 'app/core/helpers/date.helper';
import { EOrderStatus } from 'app/core/enums/order.enum';

@Component({
    selector: 'app-clinic-message',
    imports: [
        ListClinicMessageComponent,
        KInputMessageComponent,
        AvatarComponent,
        ReactiveFormsModule,
        MatIcon,
        TranslatePipe,
        MatMenu,
        EmptyComponent,
        DragDropDirective,
        MatMenuModule,
    ],
    templateUrl: './clinic-message.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './clinic-message.component.scss',
})
export class ClinicMessageComponent extends BaseComponent implements OnInit {
    private readonly authenticationService = inject(AuthenticationService);
    private readonly domSanitizer = inject(DomSanitizer);
    private readonly formBuilder = inject(FormBuilder);
    private readonly injector = inject(Injector);
    private readonly modalService = inject(ModalService);
    private readonly toastrService = inject(ToastrService);
    private readonly orderDetailViewService = inject(OrderDetailViewService);

    public isMobile = signal(false);
    protected refKInputMessage = viewChild('kInputMessage', { read: KInputMessageComponent });
    private readonly refListClinicMessageComponent = viewChild(ListClinicMessageComponent);
    protected readonly order = computed(() => this.orderDetailViewService.order());
    protected readonly orderDataSelected = signal<IEmitJoinRoomClinicMessageSocket | null>(null);
    protected readonly formControlMessage = signal(this.formBuilder.control('')).asReadonly();
    private readonly valueFormControlMessage = toSignal(
        this.formControlMessage().events.pipe(
            startWith((ConvertHelper.convertObjectEmptyToNull(this.formControlMessage().getRawValue()) ?? '').trim()),
            map(() => (ConvertHelper.convertObjectEmptyToNull(this.formControlMessage().getRawValue()) ?? '').trim()),
        ),
    );
    private readonly orderFinishedChatDays = computed(() => {
        return this.orderDetailViewService.locationSettings()?.orderFinishedChatDays ?? 0;
    });
    protected readonly isFinishedOrder = computed(() => {
        const finishedDate = this.order().finishedDate;
        const locationId = this.order().locationId;
        const days = this.orderFinishedChatDays();

        if (!finishedDate) {
            return [EOrderStatus.FINISHED, EOrderStatus.DELIVERED, EOrderStatus.CANCELLED].includes(this.order().status);
        }
        const maxFinishedDate = KDateAdd(parseDateWithTime(finishedDate), 'days', days);
        return !KDateIs(new Date(), 'beforeAndEqual', maxFinishedDate);
    });
    protected readonly customerEqualUser = computed(
        () => this.orderDetailViewService.order()?.customer?.userId === this.authenticationService?.user()?.id,
    );
    protected readonly viewFooter = computed(() => {
        const enableSend = computed(() => !!this.valueFormControlMessage());
        const enableFile = computed(() => !enableSend());
        const user = this.authenticationService.user();
        const customerEqualUser = this.customerEqualUser();
        const isFinishedOrder = this.isFinishedOrder();

        return {
            enabled: !customerEqualUser && !isFinishedOrder,
            disabledMessage: this.translateKey(isFinishedOrder ? 'ORDER_FINISHED' : 'ERROR_USER_LOGGED_EQUAL_CUSTOMER'),
            name: user.kFullName(),
            avatarUrl: user.avatar?.url,
            placeholder: this.translateKey('FORM.FIELDS.MESSAGE'),
            enableSend,
            enableFile,
        };
    });

    ngOnInit(): void {
        this.registerOnTranslate('ORDER_CLINIC_MESSAGE', portuguese, english, spanish);
        effectQueryParams(
            this.order,
            ['id', 'locationId', 'laboratoryId'],
            {
                fnc: (parameter) => {
                    if (!!parameter?.id) {
                        this.orderDataSelected.set({
                            orderId: parameter.id,
                            locationId: parameter.locationId,
                            laboratoryId: parameter.laboratoryId,
                        });
                    }
                },
            },
            { injector: this.injector },
        );
    }

    protected uploadRaw(input: HTMLInputElement): void {
        const previewMedia: IMediaPreview[] = [];
        const files = input.files as FileList;

        if (files[0].size > MAX_FILE_SIZE) {
            this.toastrService.show({ type: 'error', message: this.translateKey('TOASTR.ERRORS.MAX_FILE_SIZE') });
            return;
        }

        if (files.length > 15) {
            input.value = null;
            this.toastrService.show({
                type: 'error',
                message: this.translateKey('TOASTR.ERRORS.MAX_FILE'),
            });
            return;
        }

        for (let iFile = 0; iFile < files.length; iFile++) {
            const file = files.item(iFile);

            previewMedia.push({
                id: HashHelper.uuidv4(),
                file: file as File,
                type: FileHelper.getMediaTypeByName(file.name),
                provider: FileHelper.getProviderByName(file.name),
                progress: 0,
                url: this.domSanitizer.bypassSecurityTrustUrl(window.URL.createObjectURL(file)),
            });
        }
        input.value = null;
        const config: IModalConfig = {
            title: this.translateKey('MODAL.PREVIEW.TITLE'),
            type: 'full-screen',
            backgroundColor: 'var(--kiwid-bg-page)',
            footerColor: 'var(--kiwid-surface)',
            component: new ComponentPortal<ModalPreviewComponent>(ModalPreviewComponent),
            buttons: [
                {
                    id: 2,
                    text: this.translateKey('MODAL.GENERAL.BUTTONS.CANCEL'),
                    type: 'secondary',
                },
                {
                    id: 1,
                    text: this.translateKey('MODAL.GENERAL.BUTTONS.CONFIRM'),
                    type: 'primary',
                },
            ],
        };

        const dialog = this.modalService.openModal(config);

        this.subscriptions.add(
            dialog.componentInstance.instanceObservable<ModalPreviewComponent>().subscribe((data) => {
                data.dialog = signal(dialog);
                data.translate = this.translate;
                data.previewMedia = signal(previewMedia);
                data.user = signal(this.authenticationService.user());
                data.orderId = signal(this.orderDataSelected().orderId);
            }),
        );

        this.subscriptions.add(
            dialog
                .afterOpened()
                .pipe(first())
                .subscribe(() => {
                    this.subscriptions.add(
                        dialog.componentInstance.buttonEventClick.subscribe((button) => {
                            if (button.id === 2) {
                                dialog.close();
                                return;
                            }
                            this.refListClinicMessageComponent().createMessageFile(
                                dialog.componentInstance.instance<ModalPreviewComponent>().previewMedia(),
                            );
                            dialog.close();
                        }),
                    );
                }),
        );

        this.subscriptions.add(
            dialog
                .afterClosed()
                .pipe(first())
                .subscribe(() => {
                    this.setTitle();
                }),
        );
    }

    protected uploadImageOrVideos(input: HTMLInputElement): void {
        const previewMedia: IMediaPreview[] = [];
        const files = input.files as FileList;

        if (files.length > 15) {
            input.value = null;
            this.toastrService.show({
                type: 'error',
                message: this.translateKey('TOASTR.ERRORS.MAX_FILE'),
            });
            return;
        }

        for (let iFile = 0; iFile < files.length; iFile++) {
            const file = files.item(iFile);

            if (FileHelper.isRaw(file.name)) {
                input.value = null;
                this.toastrService.show({
                    type: 'error',
                    message: this.translateKey('TOASTR.ERRORS.PHOTO_VIDEO'),
                });
                return;
            }

            previewMedia.push({
                id: HashHelper.uuidv4(),
                file: file as File,
                type: FileHelper.getMediaTypeByName(file.name),
                provider: EMediaProvider.AWS,
                progress: 0,
                url: this.domSanitizer.bypassSecurityTrustUrl(window.URL.createObjectURL(file)),
            });
        }
        input.value = null;
        const config: IModalConfig = {
            title: this.translateKey('MODAL.PREVIEW.TITLE'),
            type: 'full-screen',
            backgroundColor: 'var(--kiwid-bg-page)',
            footerColor: 'var(--kiwid-surface)',
            component: new ComponentPortal<ModalPreviewComponent>(ModalPreviewComponent),
            buttons: [
                {
                    id: 2,
                    text: this.translateKey('MODAL.GENERAL.BUTTONS.CANCEL'),
                    type: 'secondary',
                },
                {
                    id: 1,
                    text: this.translateKey('MODAL.GENERAL.BUTTONS.CONFIRM'),
                    type: 'primary',
                },
            ],
        };

        const dialog = this.modalService.openModal(config);

        this.subscriptions.add(
            dialog.componentInstance.instanceObservable<ModalPreviewComponent>().subscribe((data) => {
                data.dialog = signal(dialog);
                data.translate = this.translate;
                data.previewMedia = signal(previewMedia);
                data.user = signal(this.authenticationService.user());
                data.orderId = signal(this.orderDataSelected().orderId);
            }),
        );

        this.subscriptions.add(
            dialog
                .afterOpened()
                .pipe(first())
                .subscribe(() => {
                    this.subscriptions.add(
                        dialog.componentInstance.buttonEventClick.subscribe((button) => {
                            if (button.id === 2) {
                                dialog.close();
                                return;
                            }

                            this.refListClinicMessageComponent().createMessageFile(
                                dialog.componentInstance.instance<ModalPreviewComponent>().previewMedia(),
                            );
                            dialog.close();
                        }),
                    );
                }),
        );

        this.subscriptions.add(
            dialog
                .afterClosed()
                .pipe(first())
                .subscribe(() => {
                    this.setTitle();
                }),
        );
    }

    protected filesDropped(data: FileHandle[]): void {
        const files = data.map((file) => file.file);
        const previewMedia: IMediaPreview[] = [];

        if (files[0].size > MAX_FILE_SIZE) {
            this.toastrService.show({ type: 'error', message: this.translateKey('TOASTR.ERRORS.MAX_FILE_SIZE') });
            return;
        }

        if (files.length > 15) {
            this.toastrService.show({
                type: 'error',
                message: this.translateKey('TOASTR.ERRORS.MAX_FILE'),
            });
            return;
        }

        for (let iFile = 0; iFile < files.length; iFile++) {
            const file = files[iFile];

            previewMedia.push({
                id: HashHelper.uuidv4(),
                file: file as File,
                type: FileHelper.getMediaTypeByName(file.name),
                provider: FileHelper.getProviderByName(file.name),
                progress: 0,
                url: this.domSanitizer.bypassSecurityTrustUrl(window.URL.createObjectURL(file)),
            });
        }

        const config: IModalConfig = {
            title: this.translateKey('MODAL.PREVIEW.TITLE'),
            type: 'full-screen',
            backgroundColor: 'var(--kiwid-bg-page)',
            footerColor: 'var(--kiwid-surface)',
            component: new ComponentPortal<ModalPreviewComponent>(ModalPreviewComponent),
            buttons: [
                {
                    id: 2,
                    text: this.translateKey('MODAL.GENERAL.BUTTONS.CANCEL'),
                    type: 'secondary',
                },
                {
                    id: 1,
                    text: this.translateKey('MODAL.GENERAL.BUTTONS.CONFIRM'),
                    type: 'primary',
                },
            ],
        };

        const dialog = this.modalService.openModal(config);

        this.subscriptions.add(
            dialog.componentInstance.instanceObservable<ModalPreviewComponent>().subscribe((data) => {
                data.dialog = signal(dialog);
                data.translate = this.translate;
                data.previewMedia = signal(previewMedia);
                data.user = signal(this.authenticationService.user());
                data.orderId = signal(this.orderDataSelected().orderId);
            }),
        );

        this.subscriptions.add(
            dialog
                .afterOpened()
                .pipe(first())
                .subscribe(() => {
                    this.subscriptions.add(
                        dialog.componentInstance.buttonEventClick.subscribe((button) => {
                            if (button.id === 2) {
                                dialog.close();
                                return;
                            }
                            this.refListClinicMessageComponent().createMessageFile(
                                dialog.componentInstance.instance<ModalPreviewComponent>().previewMedia(),
                            );
                            dialog.close();
                        }),
                    );
                }),
        );

        this.subscriptions.add(
            dialog
                .afterClosed()
                .pipe(first())
                .subscribe(() => {
                    this.setTitle();
                }),
        );
    }

    protected sendMessage(): void {
        const message = this.valueFormControlMessage();

        if (!message) {
            return;
        }

        this.subscriptions.add(
            this.refListClinicMessageComponent()
                .createMessage({
                    message,
                })
                .pipe(first())
                .subscribe(),
        );
        this.formControlMessage().patchValue('');
    }
}
